import type { InvoiceTimeZone, MockInvoiceRow } from './types';

// Retained Bubble invoice=true options, not enabled checkout methods or API enums.
export const mockPaymentTypes = [
  { value: 'pay_paypal', label: 'Paid by PayPal' },
  { value: 'pay_stripe', label: 'Paid by Stripe' },
  { value: 'cart_cloud_payments', label: 'Paid by Cloud Payments' },
  { value: 'cart_credit', label: 'Paid by Credit' },
  { value: 'pay_sync__card_0', label: 'Paid via Card' },
  { value: 'pay_shopify', label: 'Paid by Shopify' },
  { value: 'pay_apple_app_store', label: 'Paid by Apple App Store' },
  { value: 'pay_google_play', label: 'Paid by Google Play' },
  { value: 'pay_weechat', label: 'Paid by WeeChat' },
];

export const mockPaymentOrigins = [
  { value: 'bubble', label: 'Bubble' },
  { value: 'link_from_knack', label: 'Link from Knack' },
  { value: 'knack_sync', label: 'Knack sync' },
];

export interface InvoiceFilters {
  type?: string;
  origin?: string;
  dateFrom?: string;
  dateTo?: string;
  amountGreaterThan?: string;
  amountLessThan?: string;
  amountEqualTo?: string;
}

/** Fixture compatibility alias. Live Invoice queries use InvoiceFilters. */
export type MockInvoiceFilters = InvoiceFilters;

/** Mock USD search: blank omits the filter; invalid decimal input returns NaN. */
export function mockInvoiceAmountCents(value: string | undefined): number | undefined {
  const normalized = value?.trim();
  if (!normalized) return undefined;
  if (!/^(?:\d+|\d*[.,]\d{1,2})$/.test(normalized)) return NaN;
  const [whole, fraction = ''] = normalized.replace(',', '.').split('.');
  const cents = Number(whole || '0') * 100 + Number(fraction.padEnd(2, '0'));
  return Number.isSafeInteger(cents) ? cents : NaN;
}

/** Calendar boundary in the selected zone; local days may be 23 or 25 hours long. */
export function invoiceDateBoundary(
  value: string,
  end: boolean,
  timeZone: InvoiceTimeZone = 'local'
): number {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return NaN;
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(0);
  if (timeZone === 'utc') {
    date.setUTCFullYear(year, month - 1, day);
    date.setUTCHours(end ? 23 : 0, end ? 59 : 0, end ? 59 : 0, 0);
    return date.getUTCFullYear() === year &&
      date.getUTCMonth() === month - 1 &&
      date.getUTCDate() === day
      ? date.getTime()
      : NaN;
  }
  date.setFullYear(year, month - 1, day);
  date.setHours(end ? 23 : 0, end ? 59 : 0, end ? 59 : 0, 0);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day
    ? date.getTime()
    : NaN;
}

export function invalidInvoiceFilters(
  filters: InvoiceFilters,
  timeZone: InvoiceTimeZone = 'local'
): (keyof InvoiceFilters)[] {
  const invalid: (keyof InvoiceFilters)[] = [];
  if (Number.isNaN(mockInvoiceAmountCents(filters.amountEqualTo))) invalid.push('amountEqualTo');
  for (const key of ['amountGreaterThan', 'amountLessThan'] as const) {
    const value = filters[key];
    if (
      value &&
      (!/^(?:\d+|\d*\.\d{1,2})$/.test(value) ||
        !Number.isFinite(Number(value)) ||
        Number(value) < 0.01)
    ) {
      invalid.push(key);
    }
  }
  for (const key of ['dateFrom', 'dateTo'] as const) {
    if (
      filters[key] &&
      !Number.isFinite(invoiceDateBoundary(filters[key], key === 'dateTo', timeZone))
    )
      invalid.push(key);
  }
  if (filters.dateFrom && filters.dateTo && filters.dateFrom > filters.dateTo)
    invalid.push('dateFrom', 'dateTo');
  if (
    filters.amountGreaterThan &&
    filters.amountLessThan &&
    Number(filters.amountGreaterThan) > Number(filters.amountLessThan)
  )
    invalid.push('amountGreaterThan', 'amountLessThan');
  return invalid;
}

/** Fixture compatibility alias. */
export const invalidMockInvoiceFilters = invalidInvoiceFilters;

/** Synthetic UI adapter only. Real Payment filtering and authorization belong to the backend. */
export function filterMockInvoices(
  rows: MockInvoiceRow[],
  filters: InvoiceFilters,
  timeZone: InvoiceTimeZone = 'local'
): MockInvoiceRow[] {
  if (invalidInvoiceFilters(filters, timeZone).length) return [];
  const amountEqualToCents = mockInvoiceAmountCents(filters.amountEqualTo);
  return rows.filter((row) => {
    const date = new Date(row.paymentDate).getTime();
    return (
      (!filters.type || row.paymentType === filters.type) &&
      (!filters.origin || row.paymentOrigin === filters.origin) &&
      (!filters.dateFrom || date >= invoiceDateBoundary(filters.dateFrom, false, timeZone)) &&
      (!filters.dateTo || date <= invoiceDateBoundary(filters.dateTo, true, timeZone)) &&
      (!filters.amountGreaterThan || row.amountPaid > Number(filters.amountGreaterThan)) &&
      (!filters.amountLessThan || row.amountPaid < Number(filters.amountLessThan)) &&
      (amountEqualToCents === undefined || Math.round(row.amountPaid * 100) === amountEqualToCents)
    );
  });
}
